import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IMAGES, SITE } from "../mock";
import Seo from "../components/Seo";

const About = () => {
  return (
    <div className="bg-[#0E0E0F] text-[#EDE3CB]">
      <Seo
        path="/about"
        title="About"
        description="Jesse J. Adams started in the property department in 2008 and worked his way through every level — researcher, prop assistant, Assistant Property Master, and ultimately Property Master. 15+ years of film and television, mentored by a 35-year veteran."
      />
      {/* Header */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <div className="section-label">About</div>
          <h1 className="font-display text-6xl md:text-8xl mt-4 leading-[0.9]">A craft-first <br /><span className="brass-text">department head</span>.</h1>
        </div>
        <p className="lg:col-span-5 font-serif-accent italic text-2xl leading-snug text-[#EDE3CB]/85">
          Props — the unsung hero of film making.
        </p>
      </section>

      {/* Portrait + bio */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="relative lg:sticky lg:top-24">
            <img src={IMAGES.portrait} alt="Jesse J. Adams on set" className="w-full aspect-square object-cover" />
            <div className="absolute -bottom-5 -right-5 hidden md:block bg-[#0E0E0F] border border-[#B8935A] px-5 py-3">
              <div className="section-label">15+ Years</div>
              <div className="font-display text-2xl mt-1">In the Department</div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-6 text-[#EDE3CB]/85 text-[17px] leading-[1.75]">
          <p>
            I first found my way into the entertainment world through high school theatre, where I began to understand how much the objects an actor handles can shape a performance. At the time, I never thought of props as a career. It felt more like an extension of arts and crafts class — making things, solving creative problems, and helping bring an idea to life with my hands.
          </p>
          <p>
            That changed when I made my first film prop for director Harold Ramis, saw it on screen, and immediately felt something click. I realized this was where my talents, instincts, and passion all came together.
          </p>
          <p>
            I entered the film and television property department in 2008 as a production assistant, where the role of "Property Researcher" was created for the kind of detailed reference work I was doing. From there, I worked my way step by step through every level of the department — researcher, prop assistant, Assistant Property Master, and ultimately Property Master in 2022. That path gave me a true ground-level understanding of the work: research, sourcing, fabrication, graphics, set support, continuity, budgeting, storage, crew communication, vendor relationships, and wrap.
          </p>
          <p>
            Over more than 15 years in film and television, I've worked across comedy, drama, action, science fiction, and period pieces for features, television, music videos, and commercials. I was also mentored by a 35-year veteran Property Master. That mentorship shaped the way I approach every production: respect the craft, protect the budget, support the story, and lead a department people can rely on.
          </p>
          <p>
            As a department head, I take pride in building the right team for each project. I choose crew members not only for their experience, but for their professionalism, attitude, communication style, and ability to work well under pressure. I've often been known as the calm, approachable person in the room, and I value that. Collaboration and respect usually get better results than ego or force, but experience has also taught me when to stand firm, protect the integrity of the work, and advocate for what the department needs.
          </p>
          <p>
            My mind is always working through the next problem, the next detail, and the next way to support the story, the cast, and the production — often long after the day wraps. My work combines traditional property department experience with modern tools like 3D printing and scanning, Adobe Creative Suite, custom paper props, graphics, and in-house builds designed to solve problems quickly and keep costs manageable. I also bring an archive-minded approach to asset care, research, preservation, and historical accuracy, with work that has become part of The Walt Disney Company Archives.
          </p>
          <p>
            Inside and out, I know this department, and I care deeply about the work. I see what I do as an extension of my heart and soul — leaving small, detailed fingerprints across the productions I'm part of. Maybe someone notices them, maybe they don't. But I notice.
          </p>
          <p>
            I do it because I love the work, and because I love being part of a collaborative environment where everyone is pushing toward the same goal: making the project the best it can be. I know from experience that the things we make can touch people, help people, and mean something to people.
          </p>

          <div className="pt-4">
            <div className="brass-divider opacity-50 max-w-md" />
            <p className="font-serif-accent italic text-2xl md:text-3xl text-[#EDE3CB] leading-snug mt-6">
              What a wonderful gift to be a part of a make-believe world that is better than our own.
            </p>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="section-label">Affiliations + Credentials</div>
          <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">Trained, <br /><span className="brass-text">certified, ready</span>.</h2>
        </div>
        <ul className="lg:col-span-7 space-y-4 text-lg">
          {[
            "IATSE Local 44 — Property Master",
            "Former Secretary of the Property Masters Guild",
            "Member of the Academy of Television Arts & Sciences",
            "Mentored under a veteran Property Master with 35+ years of experience",
            "Current EFP Firearms License",
            "Current IATSE Safety Pass",
            "Adobe Creative Suite, 3D printing, custom fabrication",
          ].map((c) => (
            <li key={c} className="flex items-baseline gap-5 border-b border-white/5 pb-4">
              <span className="text-[#B8935A] mt-1 w-1.5 h-1.5 rounded-full bg-[#B8935A] shrink-0 self-center" />
              <span className="text-[#EDE3CB]/85">{c}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20">
        <div className="border border-[#B8935A]/30 p-10 md:p-16 text-center">
          <div className="section-label">Get in touch</div>
          <h3 className="font-display text-4xl md:text-6xl mt-4 leading-[0.95] max-w-3xl mx-auto">Tell me about the project.</h3>
          <p className="text-[#EDE3CB]/70 mt-4">Experience filming in California, Georgia, New York, Louisiana and Illinois.</p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <Link to="/contact" className="btn-primary">Check Availability <ArrowUpRight size={14} /></Link>
            <Link to="/resume" className="btn-secondary">View Resume</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
